import {
  BugReport as BugReportIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  Support as SupportIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Stack,
  Typography,
  keyframes,
  styled,
} from "@mui/material";
import React, { useState } from "react";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error | null;
  errorInfo?: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<
  React.PropsWithChildren<object>,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren<object>) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo });
    // Aqui você pode logar o erro em um serviço externo se desejar
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorScreen
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReload={this.handleReload}
          onGoHome={this.handleGoHome}
        />
      );
    }
    return this.props.children;
  }
}

interface ErrorScreenProps {
  error?: Error | null;
  errorInfo?: React.ErrorInfo | null;
  onReload: () => void;
  onGoHome: () => void;
}

// Animações
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Styled Components seguindo o design system
const ErrorContainer = styled(Box)`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f2f5;
  padding: 24px;

  @media (max-width: 600px) {
    padding: 16px;
  }
`;

const ErrorCard = styled(Card)`
  max-width: 560px;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  animation: ${fadeIn} 0.4s ease-out;
`;

const IconContainer = styled(Box)`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d32f2f 0%, #c62828 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  box-shadow: 0 4px 12px rgba(211, 47, 47, 0.25);
  animation: ${pulse} 2s ease-in-out infinite;

  @media (max-width: 600px) {
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
  }
`;

const Title = styled(Typography)`
  font-weight: 600;
  color: #111b21;
  margin-bottom: 8px;
  font-size: 24px;

  @media (max-width: 600px) {
    font-size: 20px;
  }
`;

const Description = styled(Typography)`
  color: #667781;
  font-size: 15px;
  line-height: 1.5;
  margin-bottom: 24px;
`;

const ActionButton = styled(Button)`
  text-transform: none;
  font-weight: 500;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 15px;

  &.primary {
    background-color: #008069;

    &:hover {
      background-color: #006d5b;
    }
  }

  &.secondary {
    border-color: #008069;
    color: #008069;

    &:hover {
      border-color: #006d5b;
      background-color: rgba(0, 128, 105, 0.08);
    }
  }
`;

const StatusChip = styled(Chip)`
  font-size: 12px;
  height: 28px;
  font-weight: 500;
  border-radius: 6px;

  &.error-chip {
    background-color: #ffebee;
    color: #c62828;
    border: 1px solid #ffcdd2;
  }

  &.success-chip {
    background-color: #e8f5e9;
    color: #2e7d32;
    border: 1px solid #c8e6c9;
  }
`;

const SupportAlert = styled(Alert)`
  background-color: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 8px;

  .MuiAlert-icon {
    color: #1976d2;
  }

  .MuiAlert-message {
    color: #1565c0;
    font-size: 14px;
  }
`;

const DetailsButton = styled(Button)`
  text-transform: none;
  color: #667781;
  font-size: 14px;
  justify-content: space-between;
  padding: 8px 0;

  &:hover {
    background-color: transparent;
    color: #008069;
  }
`;

const DetailsBox = styled(Box)`
  margin-top: 16px;
  padding: 16px;
  background-color: #f0f2f5;
  border-radius: 8px;
  border: 1px solid #e9edef;
`;

const ErrorPre = styled("pre")`
  font-size: 12px;
  color: #111b21;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: "Monaco", "Consolas", "Courier New", monospace;
  background-color: #ffffff;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e9edef;
  overflow: auto;
  max-height: 300px;
  margin: 12px 0 0 0;
`;

const ErrorScreen: React.FC<ErrorScreenProps> = ({
  error,
  errorInfo,
  onReload,
  onGoHome,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const isDevelopment = process.env.NODE_ENV === "development";

  return (
    <ErrorContainer>
      <ErrorCard>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3} alignItems="center" textAlign="center">
            {/* Ícone animado */}
            <IconContainer>
              <ErrorOutlineIcon
                sx={{ fontSize: { xs: 36, sm: 48 }, color: "white" }}
              />
            </IconContainer>

            {/* Título e descrição */}
            <Box>
              <Title variant="h4">Ops! Algo deu errado</Title>
              <Description variant="body1">
                Encontramos um problema inesperado. Não se preocupe, nossa
                equipe foi notificada.
              </Description>
            </Box>

            {/* Status chips */}
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              justifyContent="center"
            >
              <StatusChip
                className="error-chip"
                icon={<BugReportIcon sx={{ fontSize: 16 }} />}
                label="Erro Reportado"
                size="small"
              />
              <StatusChip
                className="success-chip"
                icon={<SupportIcon sx={{ fontSize: 16 }} />}
                label="Suporte Ativo"
                size="small"
              />
            </Stack>

            {/* Ações principais */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ width: "100%", maxWidth: 400 }}
            >
              <ActionButton
                className="primary"
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={onReload}
                fullWidth
              >
                Tentar Novamente
              </ActionButton>
              <ActionButton
                className="secondary"
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={onGoHome}
                fullWidth
              >
                Ir para Início
              </ActionButton>
            </Stack>

            {/* Informações de suporte */}
            <SupportAlert severity="info" sx={{ width: "100%" }}>
              <Typography variant="body2" component="span">
                <strong>Precisa de ajuda?</strong> Entre em contato com nosso
                suporte técnico.
              </Typography>
            </SupportAlert>

            {/* Detalhes técnicos (apenas em desenvolvimento) */}
            {isDevelopment && error && (
              <>
                <Divider sx={{ width: "100%", borderColor: "#e9edef" }} />

                <Box sx={{ width: "100%" }}>
                  <DetailsButton
                    fullWidth
                    variant="text"
                    endIcon={
                      showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    }
                    onClick={() => setShowDetails(!showDetails)}
                  >
                    <Typography variant="body2">
                      {showDetails ? "Ocultar" : "Mostrar"} detalhes técnicos
                    </Typography>
                  </DetailsButton>

                  <Collapse in={showDetails}>
                    <DetailsBox>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#d32f2f",
                          fontWeight: 600,
                          mb: 1,
                          fontSize: 13,
                        }}
                      >
                        Detalhes do Erro:
                      </Typography>
                      <ErrorPre>
                        {error.toString()}
                        {errorInfo?.componentStack && (
                          <>
                            {"\n\nComponent Stack:"}
                            {errorInfo.componentStack}
                          </>
                        )}
                      </ErrorPre>
                    </DetailsBox>
                  </Collapse>
                </Box>
              </>
            )}
          </Stack>
        </CardContent>
      </ErrorCard>
    </ErrorContainer>
  );
};

export default ErrorBoundary;
